import React, { useRef, useState } from 'react';
import {
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { getCollapseInitialState } from '@src/services/sidebar';
import logoAiVoice from '@src/assets/logo/logo-AiVoice-new.svg';
import MobilePackageInfo from '@src/containers/MobilePackageInfo';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useSidebarRoutes from '@src/hooks/useSidebarRoutes';
import CommonTypography from '@src/new-components/Typography';
import {
  StyledBetaWrapper,
  StyledDrawerNew,
  StyledSidebarNew,
  StyledSidebarTooltip,
} from './index.style';
import { StyledTagNewWrapper } from '../DesktopSidebar/index.style';

const MobileSidebar = ({
  openSidebar,
  handleCloseSidebar,
  useHeaderBanner,
  headerVoucher,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { getFeatureValue } = useFeatureFlags();
  const { user } = useSelector((state) => state.auth);
  const sidebarBodyRef = useRef(null);
  const { height: heightScreen } = useWindowDimensions();
  const [collapseState, setCollapseState] = useState({});
  const [showScrollbar, setShowScrollbar] = useState(false);
  const { mobileSidebarRoutes } = useSidebarRoutes();

  const activeRoute = (routePath) =>
    matchPath(location.pathname, { path: routePath, exact: true });

  const createLinks = (aRoutes, props) =>
    aRoutes.map((route) => {
      const hasEnabledRoute = route?.featureKey
        ? getFeatureValue(route.featureKey, {
            userId: user?.id,
            email: user?.email,
            phoneNumber: user?.phoneNumber,
          })
        : true;
      if (!hasEnabledRoute) return null;

      const isActiveRoute =
        activeRoute(route.path) ||
        route.subPaths?.some((path) => activeRoute(path));

      if (route.collapse) {
        const collapse = {};
        collapse[route?.labelSidebar] = !collapseState[route?.labelSidebar];

        return (
          <ListItem
            key={route?.labelSidebar}
            className="item"
            onMouseEnter={() => setShowScrollbar(true)}
            onMouseLeave={() => setShowScrollbar(false)}
          >
            <NavLink
              to="#"
              className={classNames('item-link', {
                'collapse-active': getCollapseInitialState(route.items),
              })}
              onClick={(e) => {
                e.preventDefault();
                setCollapseState(collapse);
              }}
            >
              {openSidebar ? (
                <>
                  <route.iconSidebar
                    className={classNames('item-icon', {
                      'item-icon-active': isActiveRoute,
                    })}
                  />
                  <ListItemText
                    primary={t(route?.labelSidebar)}
                    secondary={
                      collapseState[route?.labelSidebar] ? (
                        <ArrowDropUp className="caret" />
                      ) : (
                        <ArrowDropDown className="caret" />
                      )
                    }
                    disableTypography
                    className={classNames('item-text', {
                      'item-text-active': isActiveRoute,
                    })}
                  />
                </>
              ) : (
                <StyledSidebarTooltip title={t(route?.labelSidebar)}>
                  <route.iconSidebar className="item-icon" />
                </StyledSidebarTooltip>
              )}
            </NavLink>
            <Collapse in={collapseState[route.state]} unmountOnExit>
              <List className="list collapse-list">
                {createLinks(route.items)}
              </List>
            </Collapse>
          </ListItem>
        );
      }

      if (route?.externalUrl) {
        return (
          <ListItem
            key={route?.labelSidebar}
            className="item"
            onMouseEnter={() => setShowScrollbar(true)}
            onMouseLeave={() => setShowScrollbar(false)}
          >
            <a
              href={route.path}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames('item-link', {
                'active-link': isActiveRoute,
              })}
              onClick={() => {
                if (props?.mobile) handleCloseSidebar();
              }}
            >
              {openSidebar ? (
                <>
                  <route.iconSidebar
                    className={classNames('item-icon', {
                      'item-icon-active': isActiveRoute,
                    })}
                  />
                  <div className="item-wrapper">
                    <ListItemText
                      primary={t(route?.labelSidebar)}
                      disableTypography
                      className={classNames('item-text', {
                        'item-text-active': isActiveRoute,
                      })}
                    />
                    {route.beta && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">Beta</Typography>
                      </StyledBetaWrapper>
                    )}
                    {route.isNewFeature && (
                      <StyledBetaWrapper active={isActiveRoute}>
                        <div className="beta-background" />
                        <Typography className="beta-text">New</Typography>
                      </StyledBetaWrapper>
                    )}
                  </div>
                </>
              ) : (
                <StyledSidebarTooltip title={t(route?.labelSidebar)}>
                  <route.iconSidebar className="item-icon" />
                </StyledSidebarTooltip>
              )}
            </a>
          </ListItem>
        );
      }

      return (
        <ListItem
          key={route?.labelSidebar}
          className="item"
          onMouseEnter={() => setShowScrollbar(true)}
          onMouseLeave={() => setShowScrollbar(false)}
        >
          <NavLink
            to={route.path}
            data-id={route.dataId}
            className={classNames('item-link', {
              'active-link': isActiveRoute,
            })}
            onClick={() => {
              if (props?.mobile) handleCloseSidebar();
            }}
          >
            {openSidebar ? (
              <>
                <route.iconSidebar
                  className={classNames('item-icon', {
                    'item-icon-active': isActiveRoute,
                  })}
                />
                <div className="item-wrapper">
                  <ListItemText
                    primary={t(route?.labelSidebar)}
                    disableTypography
                    className={classNames('item-text', {
                      'item-text-active': isActiveRoute,
                    })}
                  />
                  {route.beta && (
                    <StyledBetaWrapper active={isActiveRoute}>
                      <Typography className="beta-text">Beta</Typography>
                    </StyledBetaWrapper>
                  )}
                  {route.isNewFeature && (
                    <StyledTagNewWrapper>
                      <CommonTypography variant="body-12-medium">
                        {t('new')}
                      </CommonTypography>
                    </StyledTagNewWrapper>
                  )}
                </div>
              </>
            ) : (
              <StyledSidebarTooltip title={t(route?.labelSidebar)}>
                <route.iconSidebar className="item-icon" />
              </StyledSidebarTooltip>
            )}
          </NavLink>
        </ListItem>
      );
    });

  const renderSideBarItems = (props) => (
    <div
      ref={sidebarBodyRef}
      onMouseEnter={() => setShowScrollbar(true)}
      onMouseLeave={() => setShowScrollbar(false)}
      className={`sidebar-wrapper ${
        showScrollbar ? 'small-scrollbar' : 'hidden-scrollbar'
      } `}
    >
      <List className="list">{createLinks(mobileSidebarRoutes, props)}</List>
    </div>
  );

  return (
    <StyledDrawerNew
      sx={{ display: { md: 'none', xs: 'block', sm: 'none' } }}
      variant="temporary"
      anchor="left"
      open={openSidebar}
      onClose={handleCloseSidebar}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <StyledSidebarNew
        useHeaderBanner={useHeaderBanner}
        headerVoucher={headerVoucher}
        heightScreen={heightScreen}
      >
        <div className="mobile package-wrapper-mobile">
          <img
            src={logoAiVoice}
            alt="vbee tts logo"
            className="logo-img"
            width={108}
          />
          <IconButton
            aria-label="close"
            onClick={handleCloseSidebar}
            color="secondary"
            variant="outlined"
          >
            <Close className="close-button" fontSize="small" />
          </IconButton>
        </div>
        {renderSideBarItems({ mobile: true })}
        <div className="wrap-information">
          <MobilePackageInfo isShowUpgradeButton />
        </div>
      </StyledSidebarNew>
    </StyledDrawerNew>
  );
};

export default MobileSidebar;
